import {useMutation, useQuery, useQueryClient} from "react-query"
import {
    isEmergencyStatus,
    isErrorStatus,
    isNoneOperationStatus,
    isPowerDisconnectedStatus,
    RealDevice
} from "dto/realdevice/devices"
import {RealDeviceApi} from "api/real-device"
import {AxiosError} from "axios"
import {NotificationType, showNotification} from "common/Notification"
import {ApiResponse} from "api/common/response"
import {useRootStore} from "store/useRootStore"
import {calculateDeviceStatus, Counter} from "store/dashboard/DashboardStore"
import {count} from "echarts/types/src/component/dataZoom/history";

interface RegisterParam {
    device: RealDevice,
    file?: File,
    imgFiles: File[] | undefined
}

interface UpdateParam {
    device: RealDevice,
    file?: File,
    imgFiles: File[] | undefined
}

export const useRegisterRealDeviceMutation = () => {
    const client = useQueryClient()
    return useMutation(async (param: RegisterParam) => {
        const {device, file, imgFiles} = param

        if (imgFiles && imgFiles.length > 0) {
            try {
                const uploadResult = await RealDeviceApi.uploadImages(imgFiles)
                const images = uploadResult?.data || []
                device.imageFiles = images.map(image => `${image.id}`)
            } catch (error) {
                showNotification("이미지 업로드에 실패했습니다.", NotificationType.ERROR)
            }
        }

        return await RealDeviceApi.register(device)
    }, {
        onSuccess: async () => {
            await client.invalidateQueries("real-device/list")
            showNotification("단말이 등록되었습니다", NotificationType.SUCCESS)
        },
        onError: (error, variables, context) => {
            try {
                const err = error as AxiosError<ApiResponse<null>>
                showNotification(err.response?.data?.message || "단말기 등록중 오류가 발생하였습니다")
            } catch (err) {
                showNotification("단말기를 등록중 오류가 발생하였습니다")
            }
        }
    })
}

export const useUpdateRealDeviceMutation = () => {
    const client = useQueryClient()
    return useMutation(async (param: UpdateParam) => {
        const {device, file, imgFiles} = param
        let imageId = undefined
        // if (file) {
        //     const {data: response} = await RealDeviceApi.uploadImage(file)
        //     const fileResource = response.body
        //     imageId = fileResource?.id
        // }
        // device.imageId = imageId

        if (imgFiles && imgFiles.length > 0) {
            try {
                const uploadResult = await RealDeviceApi.uploadImages(imgFiles)
                const images = uploadResult?.data || []
                device.imageFiles = [...device.imageFiles || [], ...images.map(image => `${image.id}`)]
            } catch (error) {
                showNotification("이미지 업로드에 실패했습니다.", NotificationType.ERROR)
            }
        }
        return RealDeviceApi.update(device)
    }, {
        onSuccess: async () => {
            await client.invalidateQueries("real-device/list")
            showNotification("단말이 수정되었습니다", NotificationType.SUCCESS)
        },
        onError: (error, variables, context) => {
            try {
                const err = error as AxiosError<ApiResponse<null>>
                showNotification(err.response?.data?.message || "단말기 수정중 오류가 발생하였습니다")
            } catch (err) {
                showNotification("단말기 수정중 오류가 발생하였습니다")
            }
        }
    })
}

export const useDeleteRealDeviceMutation = () => {
    const client = useQueryClient()
    return useMutation((id: number) => {
        return RealDeviceApi.deleteDevice(id)
    }, {
        onSuccess: async () => {
            await client.invalidateQueries("real-device/list")
            showNotification("단말이 삭제되었습니다", NotificationType.SUCCESS)
        },
        onError: (error,) => {
            try {
                const err = error as AxiosError<ApiResponse<null>>
                showNotification(err.response?.data?.message || "단말기 삭제중 오류가 발생하였습니다")
            } catch (err) {
                showNotification("단말기 삭제중 오류가 발생하였습니다")
            }
        }
    })
}


export const useFindAllRealDevice = () => {
    const {dashboardStore} = useRootStore()
    return useQuery("real-device/list", async () => {
        const result = await RealDeviceApi.findAll()
        const devices = result?.data?.body || []
        const counter: Counter = {
            count: devices.length,
            normalCount: 0,
            emergencyCount: 0,
            errorCount: 0,
            workingCount: 0,
            powerOffCount: 0,
            ttlCount: 0,
            noneOperationCount: 0,
            powerDisconnectedCount: 0,
        }
        counter.ttlCount = devices.length
        // let emergencyDevices: RealDevice[] = []
        devices.forEach(device => {
            calculateDeviceStatus(device, counter)

        })
        dashboardStore.setCounter(counter)
        return devices
    }, {
        // refetchInterval: 20000,
        refetchInterval: 10000,
    })
}